import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, FileText, Building2, Calendar, Hash, Euro, Shield, Loader2, Search, MapPin, UserCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL;


// Parse dates from multiple formats (DD/MM/YYYY, ISO, "DD/MM/YYYY à HH:MM:SS")
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  // Format DD/MM/YYYY or DD/MM/YYYY à HH:MM:SS
  const frMatch = String(dateStr).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (frMatch) {
    return new Date(parseInt(frMatch[3]), parseInt(frMatch[2]) - 1, parseInt(frMatch[1]));
  }
  // Format YYYY-MM-DD (ISO)
  const isoDate = new Date(dateStr);
  return isNaN(isoDate.getTime()) ? null : isoDate;
};

const formatDate = (dateStr) => {
  const d = parseDate(dateStr);
  if (!d) return String(dateStr);
  return d.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
};

const DOCUMENT_TYPES_ICONS = {
  'recu-ess': Euro,
  'recu-ftpe': Euro,
  'ftpe': Shield,
  'ftpe-adhesion': Shield,
  'rcds': FileText,
  'attestation': Shield,
  'avenant': FileText,
  'devis': FileText,
  'proposition': FileText,
  'cp': FileText,
  'cg': FileText,
  'contrat-adhesion': FileText,
  'convention': FileText,
  'confirmation': FileText,
  'membre': UserCheck,
  'certificat-adhesion': UserCheck,
};

const DOC_TYPE_OPTIONS = [
  { value: 'rcds', label: 'RCDS BATICO' },
  { value: 'attestation', label: 'Attestation RCDS' },
  { value: 'recu-ess', label: 'Recu ESS' },
  { value: 'ftpe', label: 'Adhesion FTPE' },
  { value: 'recu-ftpe', label: 'Recu FTPE' },
  { value: 'cp', label: 'Conditions Particulieres' },
  { value: 'cg', label: 'Conditions Generales' },
  { value: 'proposition', label: 'Proposition' },
  { value: 'avenant', label: 'Avenant' },
  { value: 'contrat-adhesion', label: 'Contrat Adhesion' },
  { value: 'convention', label: 'Convention' },
  { value: 'confirmation', label: 'Confirmation' },
];

export default function QRCodeVerification() {
  const { documentType, documentId } = useParams();
  const [searchParams] = useSearchParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Recherche manuelle
  const [manualRef, setManualRef] = useState('');
  const [manualType, setManualType] = useState('rcds');

  const hasUrlParams = documentType && documentId;

  useEffect(() => {
    if (hasUrlParams) {
      verifyDocument(documentType, documentId);
    }
  }, [documentType, documentId]);

  const verifyDocument = async (type, id) => {
    setLoading(true);
    setError(null);
    setResult(null);
    
    try {
      const response = await fetch(`${API}/api/verifier/${type}/${id}`);
      let data;
      try {
        data = await response.json();
      } catch {
        data = null;
      }
      
      if (data && typeof data === 'object') {
        // Si l'API retourne un objet avec detail (erreur FastAPI), le transformer
        if (data.detail && !data.valide) {
          setResult({
            valide: false,
            type_document: type,
            document_id: id,
            message: data.detail
          });
        } else if (data.recu) {
          // Normaliser la réponse imbriquée du endpoint recu-ess
          const recu = data.recu;
          const benef = data.beneficiaire || {};
          const statutPaiementLabels = {
            'acquitte': 'Acquitté',
            'partiellement_paye': 'Partiel',
            'en_attente': 'En attente'
          };
          setResult({
            ...data,
            raison_sociale: benef.raison_sociale || '',
            siret: benef.siret || '',
            numero_police: recu.numero_recu || '',
            statut_paiement: statutPaiementLabels[recu.statut_paiement] || recu.statut_paiement || '',
            paiement_valide: recu.statut_paiement === 'acquitte',
            date_validite: recu.date_emission || recu.date_signature,
            date_echeance: recu.date_echeance,
            date_signature: recu.date_signature || recu.date_emission,
            date_paiement: null,
            reference_document: recu.type_dispositif || 'Reçu ESS',
            montant_ttc: recu.montant_ttc ? `${recu.montant_ttc.toFixed(2)} €` : null,
            mention_paiement: recu.statut_paiement === 'acquitte' ? 'Acquittée' : (recu.reste_a_payer > 0 ? `Reste à payer: ${recu.reste_a_payer.toFixed(2)} €` : null),
            message: data.valide ? 'Document vérifié et authentique' : (data.message || 'Document non trouvé')
          });
        } else {
          setResult(data);
        }
      } else {
        setResult({
          valide: false,
          type_document: type,
          document_id: id,
          message: "Document non trouvé dans le registre"
        });
      }
    } catch {
      setError('Erreur de connexion au serveur de vérification. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleManualSearch = (e) => {
    e.preventDefault();
    const ref = manualRef.trim();
    if (ref) {
      // Utiliser la recherche universelle pour la recherche manuelle
      verifyUniversal(ref);
    }
  };

  const verifyUniversal = async (ref) => {
    setLoading(true);
    setError(null);
    setResult(null);
    
    try {
      const response = await fetch(`${API}/api/verifier-universel/${encodeURIComponent(ref)}`);
      let data;
      try {
        data = await response.json();
      } catch {
        data = null;
      }
      
      if (data && typeof data === 'object') {
        if (data.detail && !data.valide) {
          setResult({ valide: false, document_id: ref, message: data.detail });
        } else {
          setResult(data);
        }
      } else {
        setResult({ valide: false, document_id: ref, message: "Document non trouvé" });
      }
    } catch {
      setError('Erreur de connexion au serveur de vérification.');
    } finally {
      setLoading(false);
    }
  };

  const IconComponent = DOCUMENT_TYPES_ICONS[documentType || manualType] || FileText;
  const isValid = result?.valide === true;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Logo COPPAM */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-white mb-2" data-testid="verify-title">COPPAM</h1>
          <p className="text-slate-400 text-sm">Verification de document</p>
        </div>

        {/* Formulaire de recherche manuelle */}
        {(!hasUrlParams || (result && !isValid)) && (
          <Card className="bg-slate-800/50 border-slate-700 mb-6" data-testid="manual-search-form">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-slate-200 flex items-center gap-2">
                <Search className="w-5 h-5 text-blue-400" />
                Verifier un document
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleManualSearch} className="space-y-3">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Type de document</label>
                  <select
                    data-testid="manual-doc-type"
                    value={manualType}
                    onChange={(e) => setManualType(e.target.value)}
                    className="w-full bg-slate-700 text-slate-200 border border-slate-600 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  >
                    {DOC_TYPE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Reference du document</label>
                  <input
                    data-testid="manual-doc-ref"
                    type="text"
                    value={manualRef}
                    onChange={(e) => setManualRef(e.target.value)}
                    placeholder="Ex: RCDS-2025-0419C466, BATICO-20260213-..."
                    className="w-full bg-slate-700 text-slate-200 border border-slate-600 rounded-md px-3 py-2 text-sm placeholder:text-slate-500 focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <button
                  data-testid="manual-search-btn"
                  type="submit"
                  disabled={!manualRef.trim() || loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white py-2 rounded-md text-sm font-medium transition-colors"
                >
                  {loading ? 'Verification...' : 'Verifier'}
                </button>
              </form>
            </CardContent>
          </Card>
        )}

        {loading && (
          <div className="text-center py-12">
            <Loader2 className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-4" />
            <p className="text-slate-300">Verification du document...</p>
          </div>
        )}

        {error && !loading && (
          <Card className="bg-red-900/20 border-red-500/30">
            <CardContent className="pt-6">
              <div className="text-center">
                <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-red-400 mb-2" data-testid="verify-error">Erreur de verification</h2>
                <p className="text-red-300">{error}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {result && !loading && (
          <Card className={`${isValid ? 'bg-green-900/20 border-green-500/30' : 'bg-red-900/20 border-red-500/30'}`} data-testid="verify-result">
            <CardHeader className="text-center pb-2">
              {isValid ? (
                <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-2" />
              ) : (
                <XCircle className="w-20 h-20 text-red-500 mx-auto mb-2" />
              )}
              <CardTitle className={`text-xl ${isValid ? 'text-green-400' : 'text-red-400'}`} data-testid="verify-status">
                {isValid ? 'Document Authentique' : 'Document Non Verifie'}
              </CardTitle>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {/* Message principal */}
              <div className={`p-3 rounded-lg ${isValid ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                <p className={`text-center ${isValid ? 'text-green-300' : 'text-red-300'}`}>
                  {result?.message}
                </p>
              </div>

              {/* Details du document */}
              <div className="space-y-3 mt-4">
                <div className="flex items-center gap-3 text-slate-300">
                  <IconComponent className="w-5 h-5 text-blue-400" />
                  <div>
                    <p className="text-xs text-slate-500">Type de document</p>
                    <p className="font-medium" data-testid="verify-type-document">{result.type_document}</p>
                  </div>
                </div>

                {/* Référence du document (FTPE, RCDS PIB, Avenant, RCDS BATICO, etc.) */}
                {result.reference_document && (
                  <div className="flex items-center gap-3 text-slate-300" data-testid="verify-reference-document">
                    <FileText className="w-5 h-5 text-cyan-400" />
                    <div>
                      <p className="text-xs text-slate-500">Référence du dispositif</p>
                      <p className="font-medium">{result.reference_document}</p>
                    </div>
                  </div>
                )}

                {/* Numéro de membre (certificats d'adhésion) */}
                {result.numero_membre && (
                  <div className="flex items-center gap-3 text-slate-300" data-testid="verify-numero-membre">
                    <Hash className="w-5 h-5 text-emerald-400" />
                    <div>
                      <p className="text-xs text-slate-500">N° Membre</p>
                      <p className="font-medium font-mono">{result.numero_membre}</p>
                    </div>
                  </div>
                )}

                {/* Nom complet du membre */}
                {result.nom_complet && (
                  <div className="flex items-center gap-3 text-slate-300" data-testid="verify-nom-complet">
                    <UserCheck className="w-5 h-5 text-blue-400" />
                    <div>
                      <p className="text-xs text-slate-500">Nom complet</p>
                      <p className="font-medium">{result.nom_complet}</p>
                    </div>
                  </div>
                )}

                {/* Type de membre */}
                {result.type_membre && (
                  <div className="flex items-center gap-3 text-slate-300" data-testid="verify-type-membre">
                    <Shield className="w-5 h-5 text-blue-400" />
                    <div>
                      <p className="text-xs text-slate-500">Type de membre</p>
                      <p className="font-medium">{result.type_membre}</p>
                    </div>
                  </div>
                )}

                {result.numero_police && (
                  <div className="flex items-center gap-3 text-slate-300">
                    <Hash className="w-5 h-5 text-blue-400" />
                    <div>
                      <p className="text-xs text-slate-500">Référence Dossier</p>
                      <p className="font-medium">{result.numero_police}</p>
                    </div>
                  </div>
                )}

                {result.reference_horodatage && (
                  <div className="flex items-center gap-3 text-slate-300">
                    <Hash className="w-5 h-5 text-teal-400" />
                    <div>
                      <p className="text-xs text-slate-500">Réf. Solidaire</p>
                      <p className="font-medium">{result.reference_horodatage}</p>
                    </div>
                  </div>
                )}

                {/* Raison sociale / Entreprise */}
                {(result.raison_sociale && result.raison_sociale !== 'N/A') || (result.entreprise && result.entreprise !== 'N/A') ? (
                  <div className="flex items-center gap-3 text-slate-300" data-testid="verify-entreprise">
                    <Building2 className="w-5 h-5 text-blue-400" />
                    <div>
                      <p className="text-xs text-slate-500">Entreprise</p>
                      <p className="font-medium">{result.raison_sociale || result.entreprise}</p>
                    </div>
                  </div>
                ) : null}

                {/* SIRET */}
                {result.siret && result.siret !== 'N/A' && (
                  <div className="flex items-center gap-3 text-slate-300" data-testid="verify-siret">
                    <Hash className="w-5 h-5 text-indigo-400" />
                    <div>
                      <p className="text-xs text-slate-500">SIRET</p>
                      <p className="font-medium font-mono tracking-wide">{result.siret}</p>
                    </div>
                  </div>
                )}

                {/* Statut du paiement - NOUVEAU CHAMP DEMANDÉ */}
                {result.statut_paiement && (
                  <div className={`flex items-center gap-3 p-3 rounded-lg ${
                    result.statut_paiement === 'Acquitté' ? 'bg-green-500/10 text-green-300' :
                    ['Suspendu', 'Impayé', 'Résilié'].includes(result.statut_paiement) ? 'bg-red-500/10 text-red-300' :
                    'bg-amber-500/10 text-amber-300'
                  }`} data-testid="verify-statut-paiement">
                    {result.statut_paiement === 'Acquitté' ? (
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    ) : ['Suspendu', 'Impayé', 'Résilié'].includes(result.statut_paiement) ? (
                      <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                    ) : (
                      <Euro className="w-5 h-5 text-amber-400 flex-shrink-0" />
                    )}
                    <div>
                      <p className="text-xs opacity-70">Statut du paiement</p>
                      <p className="font-semibold text-base">{result.statut_paiement}</p>
                      {result.etat_attestation && (
                        <p className={`text-sm font-bold uppercase ${result.etat_attestation.valide ? 'text-green-300' : 'text-red-300'}`} data-testid="verify-etat-attestation">{result.etat_attestation.mention}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Date de validité - NOUVEAU CHAMP DEMANDÉ */}
                {result.date_validite && (
                  <div className="flex items-center gap-3 text-slate-300" data-testid="verify-date-validite">
                    <Calendar className="w-5 h-5 text-green-400" />
                    <div>
                      <p className="text-xs text-slate-500">Date de validité</p>
                      <p className="font-medium">{formatDate(result.date_validite)}</p>
                    </div>
                  </div>
                )}

                {/* Date d'échéance - NOUVEAU CHAMP DEMANDÉ */}
                {(result.date_echeance || result.periode_fin) && (
                  <div className="flex items-center gap-3 text-slate-300" data-testid="verify-date-echeance">
                    <Calendar className="w-5 h-5 text-amber-400" />
                    <div>
                      <p className="text-xs text-slate-500">Date d'échéance</p>
                      <p className="font-medium">{formatDate(result.date_echeance || result.periode_fin)}</p>
                    </div>
                  </div>
                )}

                {/* Statut du document */}
                {result.statut && !result.statut_paiement && (
                  <div className="flex items-center gap-3 text-slate-300" data-testid="verify-statut">
                    <Shield className="w-5 h-5 text-blue-400" />
                    <div>
                      <p className="text-xs text-slate-500">Statut</p>
                      <p className="font-medium capitalize">{result.statut}</p>
                    </div>
                  </div>
                )}

                {/* Date de signature */}
                {result.date_signature && (
                  <div className="flex items-center gap-3 text-slate-300" data-testid="verify-date-signature">
                    <Calendar className="w-5 h-5 text-green-400" />
                    <div>
                      <p className="text-xs text-slate-500">Date de signature</p>
                      <p className="font-medium">{formatDate(result.date_signature)}</p>
                    </div>
                  </div>
                )}

                {/* Hash de vérification */}
                {result.hash_verification && (
                  <div className="flex items-center gap-3 text-slate-300" data-testid="verify-hash">
                    <Shield className="w-5 h-5 text-purple-400" />
                    <div>
                      <p className="text-xs text-slate-500">Hash de vérification</p>
                      <p className="font-medium font-mono text-xs tracking-wider">{result.hash_verification}</p>
                    </div>
                  </div>
                )}

                {/* Montant */}
                {result.montant_ttc && result.montant_ttc !== 'N/A' && (
                  <div className="flex items-center gap-3 text-slate-300">
                    <Euro className="w-5 h-5 text-blue-400" />
                    <div>
                      <p className="text-xs text-slate-500">Montant Global</p>
                      <p className="font-medium">{result.montant_ttc}</p>
                    </div>
                  </div>
                )}

                {result.montant_ht && result.montant_ht !== 'N/A' && (
                  <div className="flex items-center gap-3 text-slate-300">
                    <Euro className="w-5 h-5 text-blue-400" />
                    <div>
                      <p className="text-xs text-slate-500">Montant de base</p>
                      <p className="font-medium">{result.montant_ht}</p>
                    </div>
                  </div>
                )}

                {result.date_paiement && (
                  <div className="flex items-center gap-3 text-slate-300">
                    <Calendar className="w-5 h-5 text-blue-400" />
                    <div>
                      <p className="text-xs text-slate-500">Date de paiement</p>
                      <p className="font-medium">{formatDate(result.date_paiement)}</p>
                    </div>
                  </div>
                )}

                {/* Mention paiement: Acquittée / Échéancier à jour / Attestation suspendue */}
                {result.mention_paiement && !result.statut_paiement ? (
                  <div className={`flex items-center gap-3 p-3 rounded-lg ${
                    result.mention_paiement === 'Acquittee' ? 'bg-green-500/10 text-green-300' :
                    result.mention_paiement === 'Attestation suspendue' ? 'bg-red-500/10 text-red-300' :
                    'bg-blue-500/10 text-blue-300'
                  }`} data-testid="verify-mention-paiement">
                    {result.mention_paiement === 'Acquittee' ? (
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    ) : result.mention_paiement === 'Attestation suspendue' ? (
                      <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
                    )}
                    <div>
                      <p className="text-xs opacity-70">Mention paiement</p>
                      <p className="font-semibold text-base">{result.mention_paiement}</p>
                    </div>
                  </div>
                ) : result.paiement_valide !== undefined && !result.statut_paiement ? (
                  <div className="flex items-center gap-3 text-slate-300">
                    {result.paiement_valide ? (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    ) : (
                      <XCircle className="w-5 h-5 text-orange-400" />
                    )}
                    <div>
                      <p className="text-xs text-slate-500">Paiement</p>
                      <p className={`font-medium ${result.paiement_valide ? 'text-green-400' : 'text-orange-400'}`}>
                        {result.paiement_valide ? 'Valide' : 'En attente'}
                      </p>
                    </div>
                  </div>
                ) : null}

                {/* Territoire (FTPE) */}
                {result.territoire && result.territoire !== 'N/A' && (
                  <div className="flex items-center gap-3 text-slate-300">
                    <MapPin className="w-5 h-5 text-blue-400" />
                    <div>
                      <p className="text-xs text-slate-500">Territoire</p>
                      <p className="font-medium">{result.territoire}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="mt-6 pt-4 border-t border-slate-700 text-center">
                <p className="text-xs text-slate-500">
                  Verification effectuee le {new Date().toLocaleDateString('fr-FR')} a {new Date().toLocaleTimeString('fr-FR')}
                </p>
                <p className="text-xs text-slate-600 mt-1">
                  ID: {documentId || manualRef}
                </p>
              </div>

              {/* Note de lecture RCDS - affichée pour RCDS et Avenants */}
              {(result.type_document === 'rcds' || result.type_document === 'attestation' || result.type_document === 'avenant' || (result.reference_document && result.reference_document.includes('RCDS'))) && (
                <div className="mt-4 p-4 rounded-lg border border-slate-600 bg-slate-800/80" data-testid="note-lecture-rcds">
                  <h3 className="text-sm font-bold text-blue-400 mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Vérification du document — Cadre RCDS
                  </h3>
                  <p className="text-xs text-slate-300 mb-2">
                    Le document est présenté dans le cadre de l'appréciation des capacités techniques et financières du titulaire, en cohérence avec les exigences applicables au marché.
                  </p>
                  <p className="text-xs text-slate-300">
                    Le cadre RCDS, reconnu en droit civil et en économie sociale et solidaire, contribue à la continuité d'exécution et à l'analyse globale des capacités du titulaire.
                  </p>
                  {(searchParams.get('sig') || searchParams.get('role') || searchParams.get('doc')) && (
                    <div className="bg-slate-700/50 border border-slate-600 rounded-md p-2 mb-2">
                      <p className="text-xs text-slate-300">
                        {searchParams.get('doc') && (
                          <><span className="text-slate-500">Document :</span>{' '}<strong className="text-blue-400">{decodeURIComponent(searchParams.get('doc') || '').replace(/\+/g, ' ')}</strong><br/></>
                        )}
                        <span className="text-slate-500">Signataire :</span>{' '}
                        <strong className="text-white">{decodeURIComponent(searchParams.get('sig') || '').replace(/\+/g, ' ')}</strong>
                        {searchParams.get('role') && <span className="text-slate-400">, {decodeURIComponent(searchParams.get('role') || '').replace(/\+/g, ' ')}</span>}
                      </p>
                    </div>
                  )}
                  <p className="text-xs text-slate-500 mt-3 text-right italic">COPPAM — Garanties Solidaires</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Footer COPPAM */}
        <div className="text-center mt-8">
          <p className="text-slate-500 text-xs">
            &copy; {new Date().getFullYear()} COPPAM - Tous droits reserves
          </p>
        </div>
      </div>
    </div>
  );
}
